import type { ReactElement, SyntheticEvent } from 'react';
import React, { useEffect, useRef, createElement } from 'react';
import type { HandleFilterChanged } from '@/app/common/filters/core/filterComponentType.ts';
import type { FilterPill } from '@/app/common/filters/surfaces/bar/pills/types.ts';
import { isFilterSelected } from '@/app/common/filters/core/filterSelectedUtils.ts';
import { getArrowIcon } from '@/app/common/filters/surfaces/bar/pills/sortPillIcons.tsx';
import { getSelectedPillIcon } from '@/app/common/filters/surfaces/bar/pills/selectedPillIcon.ts';
import { FilterGroupFilters } from '@/app/common/filters/surfaces/forms/FilterGroupFilters.tsx';
import type { Filter, FilterDataRange } from '@/app/common/filters/core/filtersTypes.ts';
import { FILTER_TYPE } from '@/app/common/filters/core/filtersTypes.ts';
import type {
  CustomFilterGroupLabelCreators,
  CustomFilterMainCreators,
} from '@/app/common/filters/surfaces/forms/FilterGroupsComponents.tsx';
import { filtersResetValues } from '@/app/common/filters/core/filterResetValues.ts';
import Pill from '@/app/common/components/Pill';
import FilterGroupOverlay from '@/app/common/filters/surfaces/bar/pills/overlay/FilterGroupOverlay.tsx';
import { useIsModalViewport } from '@/app/components/search/utils/useSsrMedia.ts';
import { getNumberOfColumns, SLOT } from '@/app/common/filters/surfaces/columns.ts';
import { FILTER_SURFACE } from '@/app/common/filters/surfaces/forms/custom/customFilterMainCreators.ts';
import { cloneFilters } from '@/app/common/filters/core/filtersCloneUtils';
import { getSelectedValueLabelForFilters } from '@/app/common/filters/surfaces/bar/pills/filterPillLabelUtils';
import { selectionLabelForFilters } from '@/app/common/filters/surfaces/labels/selectionLabels.tsx';
import { createUpdatedDynamicFiltersByOptions } from '@/app/common/filters/core/filterUtils.ts';
import { FILTER_NAME } from '@/app/common/filters/core/filterNames.ts';
import { usePriceFilterTitle } from '@/app/common/filters/surfaces/forms/custom/usePriceFilterScale.ts';
import { useFiltersUiData } from '@/app/common/filters/surfaces/provider/filtersUiProvider.tsx';
import { newlySelectedInGroup } from '@/app/common/filters/session/selections.ts';
import { usePressedOrder } from '@/app/common/filters/session/usePressedOrder.ts';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useTranslation } from '@/app/components/translations/Translation.tsx';
import { commitDraftFilters, normaliseDraftFilters, filtersEqual } from '@/app/common/filters/core/filterDraftUtils.ts';
import { isFilterThatCanFilterItself } from '@/app/common/filters/core/filterDefinitions.ts';
import { useReopenGuard } from '@/app/common/hooks/useReopenGuard.ts';
import type { DynamicFiltersUpdateResult } from '@/app/common/filters/searchParams/updateDynamicFilters.ts';

export type OnDynamicFilterUpdate = (params: {
  updatedFilters: Filter[];
  currentPillFilters: Filter[];
  isCancelled: () => boolean;
  onComplete: (result: DynamicFiltersUpdateResult) => void;
}) => boolean;

type FilterPillsProps = {
  filterPill: FilterPill;
  customFilterMainCreators: CustomFilterMainCreators;
  customFilterLabelCreators?: CustomFilterGroupLabelCreators;
  isLoading: boolean;
  busy?: boolean;
  handleFilterChanged: HandleFilterChanged;
  useModal?: boolean;
  onDynamicFilterUpdate?: OnDynamicFilterUpdate;
  closeOnScroll?: boolean;
  hideOverlayTitle?: boolean;
};

const getLabelCreator = (
  filterPill: FilterPill,
  customFilterLabelCreators?: CustomFilterGroupLabelCreators,
  t?: ReturnType<typeof useTranslation>['t'],
  localization?: ReturnType<typeof usePageWrapperData>['localization']
): string | ReactElement<unknown> => {
  const { label, filters } = filterPill;
  const isSelected = filters.some(isFilterSelected);

  const customFilterLabelCreator = customFilterLabelCreators
    ? customFilterLabelCreators[filterPill?.filters?.[0]?.name]
    : undefined;

  // A custom creator always wins: sort has no unselected state and describes itself.
  if (customFilterLabelCreator) return createElement(customFilterLabelCreator, filterPill);

  if (!isSelected) return label;

  const selectionLabel = selectionLabelForFilters(filters);
  if (selectionLabel) return createElement(selectionLabel, filterPill);

  return getSelectedValueLabelForFilters(filters, t, localization, filterPill.selectionOrder);
};

const getSlot = (isModal?: boolean, isModalViewport?: boolean) => {
  if (isModalViewport) return SLOT.MOBILE_MODAL;
  if (isModal) return SLOT.MODAL;
  return SLOT.PILLS;
};

export const FilterGroupPill = ({
  filterPill,
  customFilterMainCreators,
  customFilterLabelCreators,
  isLoading,
  busy,
  handleFilterChanged,
  useModal,
  onDynamicFilterUpdate,
  closeOnScroll,
  hideOverlayTitle,
}: FilterPillsProps) => {
  const { isMobile, useEvoFilters, totalResults } = useFiltersUiData();
  const isModalViewport = useIsModalViewport(isMobile);
  const { localization } = usePageWrapperData();
  const { t } = useTranslation();
  const { filters, applyOnChange } = filterPill;
  const pillRef = useRef<HTMLDivElement>(null);
  const clonedFilters = cloneFilters(filters);
  const [filterPillsFilters, setFilterPillsFilters] = React.useState<Filter[]>(clonedFilters);
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const [isUpdating, setIsUpdating] = React.useState<boolean>(false);
  const [draftTotal, setDraftTotal] = React.useState<number | undefined>(undefined);
  const [isTotalPending, setIsTotalPending] = React.useState<boolean>(false);
  const draftRequestSeq = useRef(0);
  const reopenGuard = useReopenGuard();
  const pressed = usePressedOrder();
  const isDisabled = isLoading || busy || isUpdating;
  const isClearDisabled = isDisabled || !filterPillsFilters.some(isFilterSelected);
  const draftDiffers = !filtersEqual(normaliseDraftFilters(filterPillsFilters), filters);
  // The applied total only describes the draft while the two agree; otherwise it is the wrong number.
  const shownTotal = draftDiffers ? draftTotal : totalResults;
  const applyText =
    useEvoFilters && shownTotal !== undefined
      ? t('vacation.oss.filters.cta.total', { number: `${shownTotal}` })
      : undefined;
  // The CTA is the only gate on a zero-result combination; Clear refetches and re-enables it.
  const isApplyDisabled = isDisabled || (useEvoFilters && shownTotal === 0);
  const isApplyLoading = !!useEvoFilters && isTotalPending && draftDiffers;
  const isSelected = filters.some(isFilterSelected);
  const slot = getSlot(useModal, isModalViewport);
  const columns = filterPill.columns ?? getNumberOfColumns(filters, slot, [], useEvoFilters);
  const showArrow = filters.length > 0;
  const arrowIcon = getArrowIcon(filters[0]);
  // Selected pills only wear their value's icon on the evo bar, so the current bar's look is untouched.
  const iconElement =
    filterPill.icon ??
    (useEvoFilters ? getSelectedPillIcon(filterPill.id, filters, filterPill.selectionOrder) : undefined);
  const labelCreator = getLabelCreator(filterPill, customFilterLabelCreators, t, localization);
  const className = `pill pill-${filterPill.id.toLowerCase()}${isSelected ? ' selected' : ''}`;
  const priceFilterTitle = usePriceFilterTitle();
  const rangeFilter = filterPill.filters.find((f) => f.type === FILTER_TYPE.RANGE);
  // The budget range is rendered in the displayed unit, so its header follows it instead of the
  // server label (resolved per search mode, "price per person" on DP).
  const overlayTitle =
    (rangeFilter?.name === FILTER_NAME.PRICE_RANGE && priceFilterTitle) ||
    (rangeFilter ? (rangeFilter.data as FilterDataRange).label : filterPill.label);
  const id = filterPill.id.toLowerCase();
  const overlayId = useModal ? `pill-filter-modal-${id}` : `filter-group-overlay-${id}`;

  useEffect(() => {
    pressed.reset();
    setFilterPillsFilters(cloneFilters(filters));
    // Drops any response still in flight from the previous session.
    draftRequestSeq.current += 1;
    setDraftTotal(undefined);
    setIsTotalPending(false);
    setIsUpdating(false);
  }, [isOpen]);

  const handlePillClicked = () => {
    if (reopenGuard.blocked()) return;
    setIsOpen((prevState: boolean) => !prevState);
  };

  const handleModalClose = () => {
    reopenGuard.markClosed();
    setIsOpen(false);
  };

  const createDraftUpdateHandler =
    (seq: number) =>
    ({ updatedGroups, total }: DynamicFiltersUpdateResult) => {
      // Responses can still land out of order, so only the newest request may write.
      if (seq !== draftRequestSeq.current) return;

      if (updatedGroups) {
        const allUpdatedFilters = updatedGroups.flatMap((group) => ('filters' in group ? group.filters : []));

        // We need to update the filters at the options level to maintain the order of the options even if their counter value changes.
        // In this case of the user clicking on the options while the filter is open, we do not want to sort the order again.
        setFilterPillsFilters((prevFilters: Filter[]) =>
          createUpdatedDynamicFiltersByOptions({ prevFilters, allUpdatedFilters })
        );
      }

      setDraftTotal(total);
      setIsTotalPending(false);
      setIsUpdating(false);
    };

  const requestDraftRefresh = (updatedFilters: Filter[], currentPillFilters: Filter[]) => {
    if (!onDynamicFilterUpdate) return;
    // Off the evo path only a self-filtering filter has a use for the response.
    if (!useEvoFilters && !isFilterThatCanFilterItself(updatedFilters)) return;

    draftRequestSeq.current += 1;
    setIsTotalPending(true);
    const seq = draftRequestSeq.current;
    const didUpdate = onDynamicFilterUpdate({
      // Ask about the selection apply would commit, not the raw draft.
      updatedFilters: normaliseDraftFilters(updatedFilters),
      currentPillFilters: normaliseDraftFilters(currentPillFilters),
      isCancelled: () => seq !== draftRequestSeq.current,
      onComplete: createDraftUpdateHandler(seq),
    });
    if (didUpdate) setIsUpdating(true);
  };

  const handlePillFilterChanged: HandleFilterChanged = (updatedFilters) => {
    if (applyOnChange) {
      handleFilterChanged(updatedFilters);
      setIsOpen(false);
      return;
    }

    setFilterPillsFilters((prevFilters: Filter[]) => {
      const draftFilters = prevFilters.map((prevFilter) => {
        const updatedFilterNewValue = updatedFilters.find((updatedFilter) => updatedFilter.name === prevFilter.name);
        return updatedFilterNewValue || prevFilter;
      });
      pressed.record(newlySelectedInGroup(filterPill.id as string, prevFilters, draftFilters));

      return draftFilters;
    });

    requestDraftRefresh(updatedFilters, filterPillsFilters);
  };

  const handleClear = (e: SyntheticEvent) => {
    e.stopPropagation();

    const resetFilters = filtersResetValues({ filters: filterPillsFilters });
    setFilterPillsFilters(resetFilters);
    requestDraftRefresh(resetFilters, filterPillsFilters);
  };

  const handleApply = (e: SyntheticEvent) => {
    e.stopPropagation();

    if (!useEvoFilters) {
      handleFilterChanged(filterPillsFilters);
      setIsOpen(false);
      return;
    }

    commitDraftFilters(filterPillsFilters, filters, handleFilterChanged, pressed.order());
    setIsOpen(false);
  };

  const handleClearAndApply = (e: SyntheticEvent) => {
    e.stopPropagation();

    setFilterPillsFilters((prevFilters: Filter[]) => {
      const resetFilters = filtersResetValues({ filters: prevFilters });
      handleFilterChanged(resetFilters);
      return resetFilters;
    });
  };

  const filterContent = (
    <FilterGroupFilters
      filters={filterPillsFilters}
      customFilterMainCreators={customFilterMainCreators}
      isLoading={isDisabled}
      handleFilterChanged={handlePillFilterChanged}
      columns={columns}
      columnGap={slot === SLOT.MODAL ? 16 : undefined}
      surface={FILTER_SURFACE.PILL_OVERLAY}
    />
  );

  const pill = (
    <Pill
      isLoading={isLoading}
      label={labelCreator}
      handleClick={handlePillClicked}
      handleDelete={!useEvoFilters && isSelected && !applyOnChange ? handleClearAndApply : undefined}
      showArrow={showArrow}
      arrowIcon={arrowIcon}
      selected={isSelected}
      isOpen={isOpen}
      ref={pillRef}
      icon={iconElement}
      busy={busy}
      className={className}
      keepArrowWhenSelected={useEvoFilters}
      expanded={isOpen}
      controls={isOpen ? overlayId : undefined}
    />
  );

  return (
    <>
      {pill}
      <FilterGroupOverlay
        isOpen={isOpen}
        isModalViewport={isModalViewport}
        pillRef={pillRef}
        isLoading={isDisabled}
        handleClear={applyOnChange ? undefined : handleClear}
        handleApply={applyOnChange ? undefined : handleApply}
        isClearDisabled={isClearDisabled}
        applyText={applyText}
        isApplyDisabled={isApplyDisabled}
        isApplyLoading={isApplyLoading}
        filterPillFiltersComponents={filterContent}
        setIsOpen={(value) => (value ? setIsOpen(value) : handleModalClose())}
        columns={columns}
        closeOnScroll={closeOnScroll}
        forceModal={useModal}
        overlayId={overlayId}
        overlayTitle={hideOverlayTitle && !useModal ? undefined : overlayTitle}
      />
    </>
  );
};
